/**
 * The admin app's client for PermissionService, as the signed-in admin.
 *
 *   /api/admin/permissions/v1/*  →  the Studio API signs the admin's statement and forwards /v1/*
 *
 * It uses plain ids only (`slack.read`, `integration_level:slack.read`). The service adds the tenant. The service decides
 * every call: this client never checks a right, and a refusal shows the service's own word.
 */
import { APIError, getAdminAuthHeaders } from '@/api/adminClient';

const BASE_URL = import.meta.env.VITE_STUDIO_API_BASE_URL || 'http://localhost:8087';
const PERMISSIONS_URL = `${BASE_URL}/api/admin/permissions`;

export type Grant = {
  id: string;
  subject: string;
  relation: string;
  object: string;
  kind: string;
  ends_at: string | null;
  author: string;
  reason: string;
  created_at: string;
  subject_label?: string;
  author_label?: string;
};
export type GrantPage = { grants: Grant[]; count: number; total: number; next: string | null };

export type AskableLevel = { id: string; writes: boolean; scopes: string[]; operations: string[]; object: string };
export type AskableIntegration = { id: string; granter: { kind: 'admins' | 'delegates'; delegates?: string[] }; levels: AskableLevel[] };

/** One group, as GET /v1/groups shows it. */
export type Group = {
  id: string;
  name: string;
  owners: Array<{ id: string; label: string | null }>;
  member_count: number;
  synced: boolean;
  source?: 'workos' | 'okta' | 'bamboohr' | null;
};
const SYNCED_PREFIXES = ['wos-', 'okta-', 'bhr-'];
/** A synced group is read only. Any one signal is enough: the flag, the source, or the name prefix (fail closed). */
export const isSynced = (g: Pick<Group, 'name' | 'synced' | 'source'>) => g.synced || !!g.source || SYNCED_PREFIXES.some((prefix) => g.name.startsWith(prefix));

/** The most denial rows the Overview reads: DENIAL_PAGES pages of DENIAL_PAGE rows. */
export const DENIAL_PAGE = 1000;
export const DENIAL_PAGES = 5;
/** The most pages of `platform:main` rows the Overview reads for the admins. */
export const ADMIN_PAGES = 10;

export type Delegate = { person: string; added_by: string; added_at: string };

export type AccessRequest = {
  id: string;
  requester?: string;
  integration: string;
  level: string;
  reason: string;
  ends_at?: string | null;
  state: string;
  created_at: string;
  requester_label?: string;
};
export type AppRequest = { id: string; app: string; object: string; reason: string; state: string; created_at: string; requester_label?: string };

export type Check = {
  id: number;
  at: string;
  service: string;
  subject: { type: string; id: string };
  subject_label?: string;
  app: string;
  action: string;
  resource: { type: string; id: string };
  relation: string;
  answer: 'allow' | 'deny';
  reason: string;
  missing?: string[] | null;
};
export type CheckPage = { checks: Check[]; next: number | null };
export type CheckQuery = { subject?: string; resource?: string; answer?: 'allow' | 'deny'; from?: string; to?: string; limit?: number; before?: number };

/** A refusal or a failure. `message` is the text to show; `status` and `code` are what the service sent. */
export class PermissionsError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string) {
    super(refusalText(status, code));
    this.name = 'PermissionsError';
    this.status = status;
    this.code = code;
  }
}

const WORDS: Record<string, string> = {
  forbidden: 'The service refused this. You are not allowed to do it.',
  exists: 'This already exists.',
  closed: 'This request is already closed.',
  unknown_person: 'The service does not know this person.',
  not_found: 'The service did not find this.',
  not_offered: 'The integration no longer offers this level.',
  bad_request: 'The service did not accept these values.',
  unavailable: 'The service is not available. Try again later.',
};

/** The text of a refusal: the service's own words when they are a sentence, else a known word, else the status. */
export function refusalText(status: number, code: string): string {
  if (code.includes(' ')) return code;
  const known = WORDS[code];
  return known ? `${known} (${status})` : `Permissions answered ${status}${code ? `: ${code}` : ''}.`;
}

async function call<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${PERMISSIONS_URL}${path}`, {
      method,
      headers: await getAdminAuthHeaders(body !== undefined),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new PermissionsError(0, 'Permissions did not answer. Check your connection and try again.');
  }
  if (!response.ok) {
    const out = (await response.json().catch(() => undefined)) as { error?: unknown } | undefined;
    throw new PermissionsError(response.status, typeof out?.error === 'string' ? out.error : '');
  }
  return (await response.json().catch(() => undefined)) as T;
}

const q = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : '';
};
const enc = encodeURIComponent;

/** The text to show for any thrown value. */
export function errorText(error: unknown): string {
  if (error instanceof PermissionsError || error instanceof APIError || error instanceof Error) return error.message;
  return 'Something went wrong. Try again.';
}

/** The most caller grants read for one integration. */
export const CALLER_GRANT_CAP = 2000;
export type AppCallers = { rows: AppCaller[]; cut: Record<string, number>; failed: Record<string, string> };
export type AppCaller = { integration: string; app: string; env: string; grant: Grant };

/** `app:app-<hex>.published` as the app id and environment; `app:*` is every app. Any other subject is not an app. */
export function callerOf(subject: string): { app: string; env: string } | undefined {
  if (subject === 'app:*') return { app: '*', env: 'all' };
  const match = /^app:(app-[0-9a-f]{24})(?:\.(\w+))?$/.exec(subject);
  return match ? { app: match[1], env: match[2] ?? 'all' } : undefined;
}

/** The most `integration#user` rows read for one integration. */
export const VIEWER_GRANT_CAP = 500;
export type Viewer = { kind: 'everyone' } | { kind: 'group'; name: string } | { kind: 'person'; email: string };

/** `group:support#member` (or `group:support`) as `support`; undefined when the subject is not a group. */
export const groupOf = (subject: string): string | undefined => (subject.startsWith('group:') ? subject.slice(6).replace(/#member$/, '') : undefined);

/**
 * Who can see an integration, in one phrase, from its `integration#user` rows: "Everyone", the group names and the
 * number of people, or "Nobody yet" when no row exists.
 */
export function viewersSummary(rows: Grant[]): string {
  if (rows.some((g) => g.subject === 'user:*')) return 'Everyone';
  const groups = [...new Set(rows.flatMap((g) => groupOf(g.subject) ?? []))];
  const people = rows.filter((g) => g.subject.startsWith('user:')).length;
  const parts = [...groups, ...(people ? [`${people} ${people === 1 ? 'person' : 'people'}`] : [])];
  return parts.length ? parts.join(', ') : 'Nobody yet';
}

export const levelObject = (integration: string, level: string) => `integration_level:${integration}.${level}`;

/** An end date from a date input (`2026-12-31`) as an ISO time at the end of that day, or undefined for no end. */
export function endOfDay(date: string): string | undefined {
  if (!date) return undefined;
  const at = new Date(`${date}T23:59:59`);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

export const permissionsApi = {
  /** Every integration of the service's manifests with its levels. */
  async askable(): Promise<AskableIntegration[]> {
    return (await call<{ integrations: AskableIntegration[] }>('GET', '/v1/askable')).integrations;
  },
  /** One page of live grants on an object or of a person (an email or a person id). */
  grants(by: 'object' | 'subject', value: string, options: { limit?: number; after?: string | null } = {}): Promise<GrantPage> {
    return call('GET', `/v1/grants${q({ [by]: value, limit: options.limit ?? 100, after: options.after ?? undefined })}`);
  },
  /** How many live grants a level has. One call asks for one row and reads `total`. */
  async holderCount(integration: string, level: string): Promise<number> {
    return (await this.grants('object', levelObject(integration, level), { limit: 1 })).total;
  },
  /** The person id (`user:<id>`) behind an email, read from the person's grants. Undefined when the person has no grant. */
  async personRef(email: string): Promise<string | undefined> {
    const page = await this.grants('subject', email, { limit: 20 });
    return page.grants.find((g) => g.subject.startsWith('user:') && g.subject !== 'user:*')?.subject;
  },
  async delegates(integration: string): Promise<Delegate[]> {
    return (await call<{ delegates: Delegate[] }>('GET', `/v1/integrations/${enc(integration)}/delegates`)).delegates;
  },
  async addDelegate(integration: string, person: string): Promise<void> {
    await call('PUT', `/v1/integrations/${enc(integration)}/delegates/${enc(person)}`);
  },
  async removeDelegate(integration: string, person: string): Promise<void> {
    await call('DELETE', `/v1/integrations/${enc(integration)}/delegates/${enc(person)}`);
  },
  async endGrant(id: string): Promise<void> {
    await call('DELETE', `/v1/grants/${enc(id)}`);
  },
  /**
   * Give a level to a person: the same pair that an approved request writes (D170), the level and its operation.
   * If the second write fails, the first ends again and the error shows. A live operation row (409) is not an error.
   */
  async give(input: { person: string; integration: string; level: string; endsAt?: string; reason: string }): Promise<void> {
    const id = `${input.integration}.${input.level}`;
    const base = { subject: { type: 'user', id: input.person }, reason: input.reason, ...(input.endsAt ? { ends_at: input.endsAt } : {}) };
    const first = await call<{ id: string }>('POST', '/v1/grants', { ...base, relation: 'user', object: { type: 'integration_level', id } });
    try {
      await call('POST', '/v1/grants', { ...base, relation: 'grantee', object: { type: 'operation', id } });
    } catch (error) {
      if (error instanceof PermissionsError && error.status === 409) return;
      try {
        await this.endGrant(first.id);
      } catch {
        throw new PermissionsError(0, `${errorText(error)} The first grant could not be ended: end ${id} for this person by hand.`);
      }
      throw error;
    }
  },
  async decidable(): Promise<AccessRequest[]> {
    return (await call<{ requests: AccessRequest[] }>('GET', '/v1/requests/decidable')).requests;
  },
  async decidableApps(): Promise<AppRequest[]> {
    return (await call<{ requests: AppRequest[] }>('GET', '/v1/app-requests/decidable')).requests;
  },
  async decide(kind: 'request' | 'app', id: string, verdict: 'approve' | 'deny', reason?: string): Promise<void> {
    const route = kind === 'request' ? 'requests' : 'app-requests';
    await call('POST', `/v1/${route}/${enc(id)}/${verdict}`, reason ? { reason } : undefined);
  },
  /**
   * Every live caller grant of an app on an integration (`app_integration:<i>#caller`), for all integrations of the manifests.
   * One call per integration, following `next` up to CALLER_GRANT_CAP rows. An integration that fails or is cut is named
   * in `failed` or `cut`, and the rows of the others still show.
   */
  async appCallerGrants(): Promise<AppCallers> {
    const out: AppCallers = { rows: [], cut: {}, failed: {} };
    for (const integration of await this.askable()) {
      const seen: Grant[] = [];
      try {
        let after: string | null = null;
        do {
          const page: GrantPage = await this.grants('object', `app_integration:${integration.id}`, { after });
          seen.push(...page.grants);
          after = page.grants.length > 0 ? page.next : null;
        } while (after && seen.length < CALLER_GRANT_CAP);
        if (after) out.cut[integration.id] = seen.length;
      } catch (error) {
        out.failed[integration.id] = errorText(error);
        continue;
      }
      for (const grant of seen) {
        const caller = grant.relation === 'caller' ? callerOf(grant.subject) : undefined;
        if (caller) out.rows.push({ integration: integration.id, ...caller, grant });
      }
    }
    return out;
  },
  /**
   * The live `integration:<i>#user` rows: who can see the integration. The service filters a list by object only, so the
   * relation is filtered here. It follows `next` up to VIEWER_GRANT_CAP rows and throws when the cap cuts the list, so a
   * partial list is never read as "no rule".
   */
  async integrationViewers(integration: string): Promise<Grant[]> {
    const rows: Grant[] = [];
    let after: string | null = null;
    do {
      const page: GrantPage = await this.grants('object', `integration:${integration}`, { after });
      rows.push(...page.grants);
      after = page.grants.length > 0 ? page.next : null;
    } while (after && rows.length < VIEWER_GRANT_CAP);
    if (after) throw new PermissionsError(0, `More than ${VIEWER_GRANT_CAP} rows on ${integration}. Read them with the CLI.`);
    return rows.filter((g) => g.relation === 'user');
  },
  /** Let everyone, a group or a person see an integration: `integration:<i>#user`. The service refuses a bad subject. */
  async allowToSee(integration: string, who: Viewer, reason: string): Promise<void> {
    const subject = who.kind === 'everyone' ? { type: 'user', id: '*' } : who.kind === 'group' ? { type: 'group', id: who.name } : { type: 'user', id: who.email };
    await call('POST', '/v1/grants', { subject, relation: 'user', object: { type: 'integration', id: integration }, reason });
  },
  /** Make a hand group. The service refuses a synced name and a name that exists. */
  async createGroup(name: string): Promise<void> {
    await call('POST', '/v1/groups', { name });
  },
  async groups(): Promise<Group[]> {
    return (await call<{ groups: Group[] }>('GET', '/v1/groups')).groups;
  },
  /** One page of what a group may use. */
  groupGrants(name: string, after?: string | null): Promise<GrantPage> {
    return call('GET', `/v1/groups/${enc(name)}/grants${q({ limit: 100, after: after ?? undefined })}`);
  },
  /** Add a person (by email) to a group. The service refuses a synced group, an unknown person and a double. */
  async addGroupMember(name: string, email: string, reason: string): Promise<void> {
    await call('POST', '/v1/grants', { subject: { type: 'user', id: email }, relation: 'member', object: { type: 'group', id: name }, reason });
  },
  /**
   * The `admin` rows on `platform:main`. Other relations sit among them, so the read follows `next` for up to ADMIN_PAGES pages.
   * `more` is true when the cap stopped the read.
   */
  async admins(): Promise<{ admins: Grant[]; more: boolean }> {
    const admins: Grant[] = [];
    let after: string | null = null;
    for (let pages = 0; pages < ADMIN_PAGES; pages++) {
      const page: GrantPage = await this.grants('object', 'platform:main', { after });
      admins.push(...page.grants.filter((g) => g.relation === 'admin'));
      after = page.grants.length > 0 ? page.next : null;
      if (!after) return { admins, more: false };
    }
    return { admins, more: true };
  },
  /** The denials since `from`: pages of DENIAL_PAGE rows with `before`, at most DENIAL_PAGES pages. `capped` means more exist. */
  async denialsSince(from: string): Promise<{ count: number; capped: boolean }> {
    let count = 0;
    let before: number | undefined;
    for (let pages = 0; pages < DENIAL_PAGES; pages++) {
      const page = await this.checks({ answer: 'deny', from, limit: DENIAL_PAGE, before });
      count += page.checks.length;
      if (page.next === null || page.checks.length === 0) return { count, capped: false };
      before = page.next;
    }
    return { count, capped: true };
  },
  checks(query: CheckQuery): Promise<CheckPage> {
    return call('GET', `/v1/checks${q({ ...query })}`);
  },
};
